import { Alert, Button, Modal, Textarea } from '@mantine/core';
import { IconAlertCircle, IconBan, IconRotateClockwise } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import type { OutOfPmsReason } from './condition-types.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import pms from './Pms.module.css';
import work from './PmsWork.module.css';

/**
 * "Out of PMS" rozeti (K25) [UR Z20 3.3.3]: türetilir (açık breakdown, vadesi geçmiş CoC) ya da
 * gerekçeli elle işarettir; ipucu nedenleri ve elle işaretin gerekçesini söyler.
 */
export function OutOfPmsBadge({
  reasons,
  manualReason,
}: {
  reasons: readonly OutOfPmsReason[];
  manualReason: string | null;
}) {
  const { t } = useTranslation();
  if (reasons.length === 0) return null;
  const text = reasons
    .map((reason) =>
      reason === 'manual' && manualReason
        ? `${t('pms.condition.critical.reasons.manual')}: ${manualReason}`
        : t(`pms.condition.critical.reasons.${reason}`),
    )
    .join(' · ');
  return (
    <span title={text}>
      <StatusBadge tone="warning" icon={<IconBan size={12} stroke={2.2} />}>
        {t('pms.condition.critical.outOfPms')}
      </StatusBadge>
      <span className={pms.srOnly}>{text}</span>
    </span>
  );
}

/** Pencerenin gövdesi (bileşen testi sunucu çizimiyle). */
export function OutOfPmsBody({
  mode,
  reason,
  onReason,
}: {
  mode: 'mark' | 'unmark';
  reason: string;
  onReason: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.critical.${key}`);
  return (
    <>
      <p className={work.dialogBody}>{mode === 'mark' ? k('markBody') : k('unmarkBody')}</p>
      {mode === 'mark' ? (
        <Textarea
          label={k('reason')}
          required
          autosize
          minRows={2}
          maxLength={500}
          value={reason}
          onChange={(event) => onReason(event.currentTarget.value)}
        />
      ) : null}
    </>
  );
}

/**
 * "Mark Out of PMS" / "Return to PMS" (plan M7 Task 21c; K25): elle işaret gerekçe ister
 * (`OUT_OF_PMS_REASON_REQUIRED`); ekipman kartının güncellemesidir. Yalnız gemide ve kurulum
 * devrindeki ofiste (K1).
 */
export function OutOfPmsDialog({
  opened,
  mode,
  name,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  mode: 'mark' | 'unmark';
  name: string;
  onClose: () => void;
  onConfirm: (reason: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!opened) return;
    setReason('');
    setError(null);
  }, [opened]);
  const missing = mode === 'mark' && reason.trim() === '';
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(`pms.condition.critical.${mode === 'mark' ? 'markTitle' : 'unmarkTitle'}`, { name })}
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (missing) return;
          setBusy(true);
          setError(null);
          try {
            await onConfirm(mode === 'mark' ? reason.trim() : null);
          } catch (failure) {
            setError(failure);
          } finally {
            setBusy(false);
          }
        }}
      >
        <OutOfPmsBody mode={mode} reason={reason} onReason={setReason} />
        {error ? (
          <Alert mt="sm" color="red" icon={<IconAlertCircle size={16} />} role="alert">
            {errorText(error)}
          </Alert>
        ) : null}
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('pms.drawer.cancel')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={missing}
            leftSection={
              mode === 'mark' ? <IconBan size={16} /> : <IconRotateClockwise size={16} />
            }
          >
            {t(`pms.condition.critical.${mode === 'mark' ? 'mark' : 'unmark'}`)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
